import React, { useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Loader2, Upload, X, Crop } from 'lucide-react';
import { toast } from 'sonner';
import { ImageWithFallback } from '../ui/image-with-fallback';
import { CircularImageCropModal } from '../CircularImageCropModal';

/** Reusable image picker used inside the CMS section editor + page builder.
 *  Renders a URL input + upload button. On successful upload, calls onChange
 *  with the absolute CDN URL returned by /api/upload/image.
 *
 *  The crop step is OPTIONAL: admins can either pick a file and upload it
 *  as-is (fine for wide banners), or click the crop icon to circular-crop
 *  the image before upload (great for round avatars / brand marks).
 */
export function ImageUploadField({ label, value, onChange, api }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingMode, setPendingMode] = useState('direct'); // 'direct' | 'crop'

  const doUpload = async (blobOrFile, filenameHint) => {
    setUploading(true);
    const formData = new FormData();
    if (filenameHint) {
      const f = new File([blobOrFile], filenameHint, { type: blobOrFile.type || 'image/png' });
      formData.append('file', f);
    } else {
      formData.append('file', blobOrFile);
    }
    try {
      const { data } = await api.post('/api/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (pendingMode === 'crop') {
      setPendingFile(file);
      setCropOpen(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    await doUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async (blob) => {
    if (!blob) return;
    await doUpload(blob, 'image.png');
    setPendingFile(null);
  };

  const triggerPicker = (mode) => {
    setPendingMode(mode);
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <Label className="text-white">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="bg-[#000B18] border-white/20 text-white flex-1"
          placeholder="https://… or upload"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          type="button"
          variant="ghost"
          onClick={() => triggerPicker('direct')}
          disabled={uploading}
          className="text-[#00E5FF]"
          title="Upload as-is"
          data-testid="cms-image-upload-direct"
        >
          {uploading && pendingMode === 'direct' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => triggerPicker('crop')}
          disabled={uploading}
          className="text-[#FFEA00]"
          title="Upload with circular crop"
          data-testid="cms-image-upload-crop"
        >
          {uploading && pendingMode === 'crop' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crop className="w-4 h-4" />}
        </Button>
        {value && (
          <Button type="button" variant="ghost" onClick={() => onChange('')} className="text-[#FF1744]">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      {value && (
        <div className="mt-2 relative w-full h-24 rounded-lg overflow-hidden bg-[#000B18]">
          <ImageWithFallback
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            fallbackClassName="w-full h-full"
          />
        </div>
      )}
      <CircularImageCropModal
        open={cropOpen}
        onOpenChange={setCropOpen}
        file={pendingFile}
        onConfirm={handleCropConfirm}
        outputSize={512}
        title="Crop image"
      />
    </div>
  );
}

export default ImageUploadField;
